import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const FileUploadField = ({ 
  field, 
  value = [], 
  onChange, 
  error,
  focusColor 
}) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});

  // Validar arquivo
  const validateFile = (file) => {
    const errors = [];
    
    // Verificar tamanho
    if (file.size > field.fileConfig.maxSize) {
      const maxSizeMB = (field.fileConfig.maxSize / (1024 * 1024)).toFixed(1);
      errors.push(`Arquivo muito grande. Máximo: ${maxSizeMB}MB`);
    }
    
    // Verificar tipo
    if (field.fileConfig.allowedTypes.length > 0 && !field.fileConfig.allowedTypes.includes(file.type)) {
      errors.push('Tipo de arquivo não permitido');
    }
    
    return errors;
  };

  // Simular upload de arquivo
  const uploadFile = async (file) => {
    const fileId = `${Date.now()}_${file.name}`;
    
    // Simular progresso de upload
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', field.fileConfig.folder || 'uploads');
    formData.append('fieldName', field.name);

    try {
      // Simular upload progressivo
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const currentProgress = prev[fileId] || 0;
          const newProgress = Math.min(currentProgress + Math.random() * 30, 100);
          
          if (newProgress >= 100) {
            clearInterval(interval);
            
            // Adicionar arquivo à lista
            const uploadedFile = {
              id: fileId,
              name: file.name,
              size: file.size,
              type: file.type,
              url: `/uploads/${field.fileConfig.folder}/${fileId}_${file.name}`, // URL simulada
              status: 'completed'
            };
            
            const newFiles = field.fileConfig.multiple 
              ? [...(Array.isArray(value) ? value : []), uploadedFile]
              : [uploadedFile];
            
            onChange(newFiles);
            
            // Limpar progresso
            setTimeout(() => {
              setUploadProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[fileId];
                return newProgress;
              });
            }, 1000);
            
            return { ...prev, [fileId]: 100 };
          }
          
          return { ...prev, [fileId]: newProgress };
        });
      }, 200);
      
    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadErrors(prev => ({
        ...prev,
        [fileId]: 'Erro no upload do arquivo'
      }));
      
      // Limpar progresso
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
    }
  };

  // Handler para arquivos dropados ou selecionados
  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(file => {
      const validationErrors = validateFile(file);
      
      if (validationErrors.length > 0) {
        const fileId = `${Date.now()}_${file.name}`;
        setUploadErrors(prev => ({
          ...prev,
          [fileId]: validationErrors.join(', ')
        }));
        return;
      }

      // Se não permite múltiplos arquivos, limpar lista atual
      if (!field.fileConfig.multiple) {
        onChange([]);
      }

      uploadFile(file);
    });
  }, [field, value, onChange]);

  // Configurar dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: field.fileConfig.allowedTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {}),
    multiple: field.fileConfig.multiple,
    disabled: !field.fileConfig.multiple && Array.isArray(value) && value.length > 0
  });

  // Remover arquivo
  const handleRemoveFile = (fileId) => {
    const newFiles = Array.isArray(value) ? value.filter(file => file.id !== fileId) : [];
    onChange(newFiles);
    
    // Limpar erros relacionados
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileId];
      return newErrors;
    });
  };

  // Obter ícone do arquivo baseado no tipo
  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('word')) return '📝';
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return '📊';
    if (fileType.includes('text')) return '📄';
    return '📎';
  };

  // Formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const files = Array.isArray(value) ? value : [];
  const hasFiles = files.length > 0;
  const canUpload = field.fileConfig.multiple || !hasFiles;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {field.label}
        {field.required && <span style={{ color: 'red' }}> *</span>}
      </Typography>

      {/* Área de upload */}
      {canUpload && (
        <Paper
          {...getRootProps()}
          sx={{
            border: `2px dashed ${isDragActive ? focusColor || '#1976d2' : '#ccc'}`,
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: isDragActive ? 'rgba(25, 118, 210, 0.04)' : 'grey.50',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderColor: focusColor || '#1976d2'
            }
          }}
        >
          <input {...getInputProps()} />
          
          <CloudUploadIcon 
            sx={{ 
              fontSize: 48, 
              color: isDragActive ? focusColor || '#1976d2' : 'grey.400',
              mb: 1 
            }} 
          />
          
          <Typography variant="body1" gutterBottom>
            {isDragActive 
              ? 'Solte os arquivos aqui...' 
              : 'Clique para fazer upload ou arraste arquivos aqui'
            }
          </Typography>
          
          <Typography variant="caption" color="textSecondary">
            Máx: {(field.fileConfig.maxSize / (1024 * 1024)).toFixed(1)}MB
            {field.fileConfig.multiple ? ' | Múltiplos arquivos' : ' | Um arquivo'}
            {field.fileConfig.enablePdfParsing && ' | Processamento de PDF ativo'}
          </Typography>
          
          {/* Tipos permitidos */}
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
            {field.fileConfig.allowedTypes.map(type => {
              const extension = type.split('/')[1]?.toUpperCase() || type.toUpperCase();
              return (
                <Chip
                  key={type}
                  label={extension}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Lista de arquivos */}
      {(hasFiles || Object.keys(uploadProgress).length > 0 || Object.keys(uploadErrors).length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Arquivos {hasFiles && `(${files.length})`}
          </Typography>
          
          <List dense>
            {/* Arquivos carregados */}
            {files.map((file) => (
              <ListItem key={file.id} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemIcon>
                  <Typography sx={{ fontSize: '1.5rem' }}>
                    {getFileIcon(file.type)}
                  </Typography>
                </ListItemIcon>
                
                <ListItemText
                  primary={file.name}
                  secondary={`${formatFileSize(file.size)} • ${file.type}`}
                />
                
                <ListItemSecondaryAction>
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <IconButton 
                    edge="end" 
                    onClick={() => handleRemoveFile(file.id)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}

            {/* Progresso de upload */}
            {Object.entries(uploadProgress).map(([fileId, progress]) => (
              <ListItem key={fileId} sx={{ bgcolor: 'background.paper', mb: 1, borderRadius: 1 }}>
                <ListItemIcon>
                  <FileIcon />
                </ListItemIcon>
                
                <ListItemText
                  primary="Fazendo upload..."
                  secondary={
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <LinearProgress variant="determinate" value={progress} />
                      <Typography variant="caption" color="textSecondary">
                        {Math.round(progress)}%
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}

            {/* Erros de upload */}
            {Object.entries(uploadErrors).map(([fileId, errorMsg]) => (
              <ListItem key={fileId} sx={{ bgcolor: 'error.50', mb: 1, borderRadius: 1 }}>
                <ListItemIcon>
                  <ErrorIcon color="error" />
                </ListItemIcon>
                
                <ListItemText
                  primary="Erro no upload"
                  secondary={errorMsg}
                />
                
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    onClick={() => setUploadErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors[fileId];
                      return newErrors;
                    })}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {/* Erro de validação geral */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default FileUploadField;